import React, { forwardRef, useEffect, useState } from 'react';

import clsx from 'clsx';
import type { AlineInputProps } from 'src/AlineInput';
import { AutosizeInput } from 'src/components/generic/AutosizeInput';
import { composeRefs } from 'src/helpers/compose-refs';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

export enum EditableNameVariant {
  Default,
  LargeText,
}

const borderSizePx = 2;
const useStyles = makeStyles<StyleProps>()((theme, { disabled, fullWidth, variant }) => ({
  input: {
    ...getTypographyForSx(theme.typography.h2),
    '&:focus-visible': {
      border: 0,
      outline: 0,
    },
    backgroundColor: 'inherit',
    border: 0,
    fontSize: variant === EditableNameVariant.LargeText ? 20 : 16,
    maxWidth: '100%',
    outline: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: fullWidth ? '100%' : undefined,
  },
  wrapper: {
    ...(disabled
      ? {}
      : {
          '&:focus-within': {
            backgroundColor: theme.palette.common.white,
            border: `${borderSizePx}px solid ${theme.appColors.primary}`,
            borderRadius: 3,
          },
          '&:hover': {
            '&:focus-within': {
              backgroundColor: theme.palette.common.white,
              border: `${borderSizePx}px solid ${theme.appColors.primary}`,
              borderRadius: 3,
            },
            backgroundColor: theme.palette.common.white,
            border: `${borderSizePx}px solid ${theme.appColors.rule}`,
            borderRadius: 3,
          },
        }),
    backgroundColor: 'inherit',
    border: `${borderSizePx}px solid transparent`,
    borderRadius: 3,
    boxSizing: 'border-box',
    flex: fullWidth ? 1 : undefined,
    maxWidth: '100%',
    overflow: 'hidden',
    padding: theme.spacing(0.5, 1),
  },
}));

interface StyleProps {
  disabled: boolean;
  fullWidth: boolean;
  variant: EditableNameVariant;
}

interface EditableNameInputProps extends React.HtmlHTMLAttributes<HTMLElement> {
  name: string;
  updateName: (name: string) => void;
  alignCenter?: boolean;
  dataCy?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  inputBaseClassName?: string;
  onBlur?: AlineInputProps['onBlur'];
  onFocus?: AlineInputProps['onFocus'];
  placeholder?: string;
  variant?: EditableNameVariant;
  wrapperClassName?: string;
}

export const EditableNameInput = forwardRef(function EditableNameInput(
  props: EditableNameInputProps,
  ref: React.Ref<HTMLInputElement>
): JSX.Element {
  const {
    alignCenter = false,
    className,
    dataCy,
    disabled,
    fullWidth = false,
    name,
    onBlur,
    onFocus,
    placeholder = 'Enter text...',
    updateName,
    variant = EditableNameVariant.Default,
    wrapperClassName,
  } = props;
  const { classes } = useStyles({ disabled: Boolean(disabled), fullWidth, variant });
  const [isFocused, setIsFocused] = useState(false);
  const [internalName, setInternalName] = useState<string>(name);
  const debouncedUpdateName = useDebouncedCallback(updateName, 500);
  const [internalRef, setInternalRef] = useState<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isFocused) setInternalName(name);
  }, [name, isFocused]);

  return (
    <AutosizeInput
      ref={composeRefs(ref, node => setInternalRef(node))}
      className={clsx(classes.wrapper, wrapperClassName)}
      data-cy={dataCy}
      disabled={disabled}
      inputClassName={clsx(className, classes.input)}
      inputStyle={{
        overflow: 'hidden',
        textAlign: alignCenter ? 'center' : undefined,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      minWidth={1}
      placeholder={placeholder}
      style={{
        textAlign: alignCenter ? 'center' : undefined,
      }}
      value={internalName}
      onBlur={event => {
        debouncedUpdateName.flush();
        setIsFocused(false);
        onBlur?.(event);
      }}
      onChange={event => {
        setInternalName(event.target.value);
        debouncedUpdateName(event.target.value);
      }}
      onFocus={event => {
        setIsFocused(true);
        onFocus?.(event);
      }}
      onKeyDown={event => {
        switch (event.key) {
          case 'Enter':
            debouncedUpdateName.flush();
            internalRef?.blur();
            break;
          case 'Escape':
            internalRef?.blur();
            break;
          default:
            break;
        }
      }}
    />
  );
});
